import * as React from 'react';
import {useNativeNode} from '../useNativeNode';
import {TypeImplementation, Plugins} from '@packages/systems/wfdl';
import {ELiteral} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {styledDiv} from '@packages/utilities/styled';

import {useLottieIndexDB} from '@packages/systems/ui/shared-ui/Lottie/Lottie';
import {Checkbox, View, NumberInput} from '@packages/systems/spring';

import {
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';

const CheckboxInputWrapper = styledDiv(({theme: {gutters}}) => ({
  gridColumn: '1 / -1',
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: `1fr ${gutters.x4 * 13}px`,
}));

const createLiteralExpr = (value: null | number) =>
  ELiteral({
    name: qualify(Plugins.name('Animation'), typeName('AnimationDuration')),
    value,
  });

const useAnimationJson = () => {
  const nativeNode = useNativeNode();
  const src = nativeNode?.dataset?.src || null;
  const animationJson = useLottieIndexDB(src, false);
  return {animationJson, hasNativeNode: nativeNode != null};
};

export const duration = TypeImplementation.Custom({
  render: function DurationCheckbox({dispatch, expr}) {
    const {animationJson, hasNativeNode} = useAnimationJson();

    if (animationJson == null && hasNativeNode) {
      return null;
    }

    const literalVal = ContextualExpressions.getLiteral(stripAnn(expr));
    if (literalVal === null) {
      return null;
    }
    const literal = literalVal.value;
    // @ts-expect-error - TS2322 - Type 'Json' is not assignable to type 'number | null'.
    const state: number | null = literal;

    const {op = null, fr: frameRate = null} = animationJson ?? {};
    const totalFrames = op ? Math.floor(op) : null;
    const totalDuration =
      frameRate && totalFrames ? totalFrames / frameRate : 0;

    const handleNumberInputChange = (value: number | undefined) => {
      dispatch(
        updateExpressionAction(
          createLiteralExpr(value === undefined || value === 0 ? null : value)
        )
      );
    };

    const handleCheckboxChange = () => {
      dispatch(
        updateExpressionAction(
          createLiteralExpr(state === null ? totalDuration : null)
        )
      );
    };

    return (
      <CheckboxInputWrapper>
        <View gridColumn="1 / -1" gridRow="1 / 2">
          <Checkbox
            id="lottie-use-builtin-checkbox"
            label="Use built-in duration"
            isChecked={state === null}
            isDisabled={!hasNativeNode}
            onChange={handleCheckboxChange}
          />
        </View>

        <View
          gridColumn="2 / 3"
          gridRow="1 / 2"
          placeSelf="start end"
          zIndex={1}
        >
          <NumberInput
            aria-label="Lottie duration"
            isDisabled={!hasNativeNode || state === null}
            min={0}
            max={Number.MAX_SAFE_INTEGER}
            name="lottie-duration-input"
            onChange={handleNumberInputChange}
            placeholder={hasNativeNode ? String(totalDuration) : ''}
            step={1}
            value={
              state !== null ? state : hasNativeNode ? totalDuration : undefined
            }
          />
        </View>
      </CheckboxInputWrapper>
    );
  },
  system: {
    labelVariant: 'hidden',
    defaultValue: null,
  },
});
